import { SerializedPlainDate } from './api/data.types';
import { ContractType, WaterFeeType } from './contract.types';
import { RealEstateDocument } from './realEstate.types';

export type RoomType =
  | 'BATHROOM'
  | 'KITCHEN'
  | 'HALLWAY'
  | 'BEDROOM'
  | 'LIVING_ROOM';

export type FloorMaterial =
  | 'PLANK'
  | 'PARQUET'
  | 'LAMINATE'
  | 'CORK'
  | 'PLASTIC'
  | 'TILE'
  | 'MASS'
  | 'VINYL'
  | 'PLANK';

export type Room = {
  id: string;
  type: RoomType;
  floorMaterial: FloorMaterial | null;
};

/** Data returned from /realestates/v1/homes/apartments/myapartments */
export interface OwnApartment {
  productId: string;
  address: string;
  apartmentNumber: null;
  apartmentType: null;
  buildingImageURL: string;
  floorPlanURL: string;
  realestateAddress: string;
  realestateID: string;
  apartmentId: string;
  apartment: ApartmentDocument;
  realestate: RealEstateDocument;
  rooms: Room[] | null;
  stairwell: {
    doorCode: string | null;
  } | null;
  laundryHouseReservationUrl?: string | null;
}

export type TvSystem = 'CABLE' | 'TERRESTRIAL' | 'SATELLITE';

/** Apartment data returned from /realestates/v1/homes/apartments/myapartments */
export interface ApartmentDocument {
  productId: string | null;
  apartmentId: string;
  realEstateId: string;
  name: string;
  apartmentNumber: string | null;
  formattedRoomType: string;
  roomType: ApartmentRoomType;
  amountOfRooms: number;
  flexHome: FlexHome;
  livingArea: number;
  floorNumber: string | null;
  floorText: string | null;
  floors: number | null;
  staircase: string | null;
  constructionYear: number;
  completitionDate: SerializedPlainDate;
  renovationYear: number | null;
  renovationDate: SerializedPlainDate | null;
  energyClass: string | null;
  energyClassRegulationYear: number | null;
  tvSystem: TvSystem | null;
  tvSystemOperator: string | null;
  internet: Internet | null;
  isAra: boolean;
  isNew: boolean;
  isRenovated?: boolean | null;
  hasSuperintendent: boolean | null;
  hasParkingSpace?: boolean;
  parkingSpaceId?: string;
  streetAddress: string;
  mandatoryInsurance: boolean;
  status: ApartmentStatus;
  lifeCycleStatus: ApartmentLifecycleStatus;
  freeDate: string | null;
  freeDateEpoch: number;
  productStatus: ProductStatus;
  productType: ProductType;
  rent: number | null;
  campaignRent: number | null;
  collateral: number | null;
  contractType: ContractType;
  waterRate: number | null;
  waterRateType: WaterRateType;
  searchAlertTriggerTime: string | null;
  features: ApartmentFeature[];
  electricityIncludedInRent: boolean;
  gasIncludedInRent: boolean | null;
  gsrnIdentifier: string | null;
  seniorApartment: boolean;
  studentHome: boolean | null;
  fridge: Fridge | null;
  stove: Stove | null;
  dishwasher: Dishwasher | null;
  courtyardTerrace: CourtyardTerrace | null;
  washingMachine: WashingMachine | null;
  dryer: Dryer | null;
  fridgeType: FridgeType | null;
  balcony: Balcony | null;
  anomalySmokeFree: boolean | null;
}

export type WaterRateType =
  | 'PER_PERSON'
  | 'USAGE'
  | 'INCLUDED_IN_RENT'
  | 'ADVANCE_AND_COMPENSATION';

export type MatchingApartmentContractWaterKeys = {
  [key in WaterFeeType]: WaterRateType;
};

export const matchedKeysOfApartmentWaterTypes: MatchingApartmentContractWaterKeys =
  {
    INCLUDED_IN_RENT: 'INCLUDED_IN_RENT',
    ADVANCE_AND_EQUALIZATION: 'ADVANCE_AND_COMPENSATION',
    METER_IN_APARTMENT: 'USAGE',
    PER_PERSON_PER_MOUNTH: 'PER_PERSON',
  };

export const apartmentRoomTypes = [
  'TYPE_1H',
  'TYPE_2H',
  'TYPE_3H',
  'TYPE_4H',
] as const;

export type ApartmentRoomType = (typeof apartmentRoomTypes)[number];

export type ProductStatus =
  | 'UNPUBLISHED'
  | 'SHOWN_AND_NOT_RENTABLE'
  | 'SHOWN_AND_RENTABLE';
export type ApartmentStatus = 'FREE' | 'GOING_TO_BE_FREE' | 'ON_CONTRACT';
export type ApartmentLifecycleStatus = 'IN_USE' | 'IN_USAGE_BAN';
export type ProductType = 'SALE' | 'RENT';
export type PropertyType = 'SERVICE_OPEN';

export const apartmentFeatures = [
  'ELEVATOR',
  'APARTMENT_SAUNA',
  'BALCONY',
  'GLAZED_BALCONY',
  'FRENCH_BALCONY',
  'TWO_BALCONIES',
] as const;

export type ApartmentFeature = (typeof apartmentFeatures)[number];

export enum Balcony {
  YES = 'YES',
  NO = 'NO',
  FRENCH_BALCONY = 'FRENCH_BALCONY',
  GLAZED_BALCONY = 'GLAZED_BALCONY',
  TWO_BALCONIES = 'TWO_BALCONIES',
}

export enum Fridge {
  YES = 'YES',
  NO = 'NO',
  CONNECTIONS = 'CONNECTIONS',
  INTEGRATED = 'INTEGRATED',
}

export enum FridgeType {
  FRIDGE = 'FRIDGE',
  FREEZER = 'FREEZER',
  COMBINATION = 'COMBINATION',
  INTEGRATED = 'INTEGRATED',
  LOW = 'LOW',
  YANKEE = 'YANKEE',
  FRIDGE_AND_FREEZER = 'FRIDGE_AND_FREEZER',
  COMBINATION_AND_COMBINATION = 'COMBINATION_AND_COMBINATION',
}

export enum Stove {
  INDUCTION = 'INDUCTION',
  GAS = 'GAS',
  CERAMIC = 'CERAMIC',
  TRADITIONAL = 'TRADITIONAL',
  TWO_PLATE = 'TWO_PLATE',
}

export enum Dishwasher {
  YES = 'YES',
  NO = 'NO',
  CONNECTIONS = 'CONNECTIONS',
  INTEGRATED = 'INTEGRATED',
}

export enum CourtyardTerrace {
  ROOF_TERRACE = 'ROOF_TERRACE',
  COURTYARD = 'COURTYARD',
  TERRACE = 'TERRACE',
  COURTYARD_AND_TERRACE = 'COURTYARD_AND_TERRACE',
  PATIO = 'PATIO',
  NO = 'NO',
}

export enum Dryer {
  YES = 'YES',
  NO = 'NO',
  CONNECTIONS = 'CONNECTIONS',
  INTEGRATED = 'INTEGRATED',
}

export enum WashingMachine {
  INPUT_CONNECTION = 'INPUT_CONNECTION',
  OUTPUT_CONNECTION = 'OUTPUT_CONNECTION',
  INPUT_OUTPUT_CONNECTIONS = 'INPUT_OUTPUT_CONNECTIONS',
  WASHINGMACHINE_INCLUDED = 'WASHINGMACHINE_INCLUDED',
  NOT_INSTALLABLE = 'NOT_INSTALLABLE',
}

export interface FlexHome {
  isFlexHome: boolean;
  freeOfDebtPriceprovider: number;
  equity: number;
  paymentPercentag: number;
}

type InternetAgreement = 'SATO_FRAMEWORK' | 'HOUSING_ASSOCATION' | 'NONE';
export interface Internet {
  includedInRent: boolean | null;
  provider: string | null;
  speed: string | null;
  agreement: InternetAgreement | null;
}

export interface Location {
  lat: number;
  lon: number;
}
